import { z } from "zod";

import {
	ALL_KINDS as ALL_CHART_KINDS,
	type Spec as ChartSpec,
	DEFAULT_KIND as DEFAULT_CHART_KIND,
} from "../../components/widget-form/spec";
import { tuple } from "../_decoders";
import { Encoder } from "../_encoder";

import { decoder as barChartDecoder, encoder as barChartEncoder } from "./bar-chart/spec";
import { decoder as gaugeChartDecoder, encoder as gaugeChartEncoder } from "./gauge-chart/spec";
import { decoder as pieChartDecoder, encoder as pieChartEncoder } from "./pie-chart/spec";
import { decoder as statChartDecoder, encoder as statChartEncoder } from "./stat-chart/spec";
import { decoder as timeSeriesChartDecoder, encoder as timeSeriesChartEncoder } from "./time-series-chart/spec";

const decoder = z
	.union([
		tuple([z.literal(ALL_CHART_KINDS.indexOf("BarChart")), barChartDecoder]).transform(
			([_, settings]): ChartSpec => ({ kind: "BarChart", settings }),
		),

		tuple([z.literal(ALL_CHART_KINDS.indexOf("GaugeChart")), gaugeChartDecoder]).transform(
			([_, settings]): ChartSpec => ({ kind: "GaugeChart", settings }),
		),

		tuple([z.literal(ALL_CHART_KINDS.indexOf("StatChart")), statChartDecoder]).transform(
			([_, settings]): ChartSpec => ({ kind: "StatChart", settings }),
		),

		tuple([z.literal(ALL_CHART_KINDS.indexOf("PieChart")), pieChartDecoder]).transform(
			([_, settings]): ChartSpec => ({ kind: "PieChart", settings }),
		),

		tuple([z.any(), timeSeriesChartDecoder]).transform(
			([, settings]): ChartSpec => ({ kind: "TimeSeriesChart", settings }),
		),
	])
	.optional()
	.catch(undefined);

function encoder(spec: undefined | ChartSpec): Encoder {
	const encodedKind = Encoder.primitive(
		ALL_CHART_KINDS.indexOf(spec?.kind ?? DEFAULT_CHART_KIND),
		ALL_CHART_KINDS.indexOf(DEFAULT_CHART_KIND),
	);

	switch (spec?.kind) {
		case "BarChart": {
			return Encoder.tuple(encodedKind, barChartEncoder(spec.settings ?? {}));
		}

		case "GaugeChart": {
			return Encoder.tuple(encodedKind, gaugeChartEncoder(spec.settings ?? {}));
		}

		case "StatChart": {
			return Encoder.tuple(encodedKind, statChartEncoder(spec.settings ?? {}));
		}

		case "PieChart": {
			return Encoder.tuple(encodedKind, pieChartEncoder(spec.settings ?? {}));
		}

		default: {
			return Encoder.tuple(encodedKind, timeSeriesChartEncoder(spec?.settings ?? {}));
		}
	}
}

export { decoder, encoder };
